import { useCallback, useMemo } from 'react';

import { isBrandTermActive } from 'src/data/brandTerm';
import { isSameLanguage } from 'src/data/generation';
import { formatBrandVoiceChildRoute } from 'src/lib/routing';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { ROUTES } from 'src/routes';
import {
  BrandTermsStore,
  useBrandVoiceIdsForBrandTerms,
  useBrandVoicesAssets,
  useCurrentProject,
} from 'src/store';
import { useBrandVoicePopup } from 'src/views/BrandVoicePopup';

import { useBrandVoices } from '../useBrandVoices';
import { useHistoryPushOrInfinityOpen } from '../useHistoryPushOrInfinityOpen';
import { useSetupProjectVocabulary } from '../useSetupProjectVocabulary';

const useBrandTermsState = createStateSelectorHook(BrandTermsStore, ['appliedDetectedTermIds']);

export const useBrandTermsProps = ({
  detectedBrandTermsData,
  onApplyDetectedBrandTerm,
  onDetectedBrandTermHover,
  onDetectedBrandTermClick,
  disabled,
  readOnly,
  loading,
  language,
}) => {
  const currentProject = useCurrentProject();
  const { brandVoices: availableBrandVoices } = useBrandVoices();
  const { brandTerms: availableBrandTerms } = useBrandVoicesAssets();
  const { brandVoiceIdsForBrandTerms, brandRulesEnabled } = useBrandVoiceIdsForBrandTerms();
  const historyPushOrInfinityOpen = useHistoryPushOrInfinityOpen();
  const setupProjectVocabulary = useSetupProjectVocabulary({ language });
  const openBrandVoicePopup = useBrandVoicePopup();
  const { brandVoicesAssets } = useBrandVoicesAssets();

  // Filter out detected terms that have already been applied
  const { appliedDetectedTermIds } = useBrandTermsState();
  const notAppliedDetectedTerms = useMemo(
    () => detectedBrandTermsData?.items?.filter((term) => !appliedDetectedTermIds.has(term.id)),
    [appliedDetectedTermIds, detectedBrandTermsData?.items]
  );

  const brandVoicesForLanguage = availableBrandVoices.filter((brandVoice) =>
    isSameLanguage(brandVoice.language, language)
  );

  const brandVoiceExistForLanguage = brandVoicesForLanguage.length > 0;

  const availableBrandTermsCount =
    availableBrandTerms.filter((brandTerm) =>
      isSameLanguage(brandTerm?.brandVoiceLanguage, language)
    ).length || 0;
  const projectType = currentProject?.type;

  const activeBrandVoices = useMemo(() => {
    if (!brandRulesEnabled || !brandVoiceIdsForBrandTerms.length) {
      return [];
    }

    const firstSelectedBrandVoice = brandVoicesAssets.find((brandVoice) =>
      brandVoiceIdsForBrandTerms.includes(brandVoice.id)
    );

    return brandVoicesAssets.filter((brandVoice) =>
      isSameLanguage(firstSelectedBrandVoice?.language, language)
        ? brandVoiceIdsForBrandTerms.includes(brandVoice.id)
        : isSameLanguage(brandVoice.language, language) &&
          brandVoice.brandTerms?.some(isBrandTermActive)
    );
  }, [brandRulesEnabled, brandVoiceIdsForBrandTerms, brandVoicesAssets, language]);

  // Handle "empty terms" CTA click, either open "vocabulary" settings or navigate to "brand terms" page
  const handleEmptyTermsClick = useCallback(async () => {
    if (brandVoiceExistForLanguage) {
      const route = formatBrandVoiceChildRoute(brandVoicesForLanguage[0].id, ROUTES.BRAND_TERMS);
      historyPushOrInfinityOpen(route);
    } else {
      const newBrandVoice = await openBrandVoicePopup({
        noRedirect: true,
        language,
        lockLanguage: true,
      });
      if (!newBrandVoice) return;
      const route = formatBrandVoiceChildRoute(newBrandVoice.id, ROUTES.BRAND_TERMS);
      historyPushOrInfinityOpen(route);
    }
  }, [
    brandVoiceExistForLanguage,
    brandVoicesForLanguage,
    historyPushOrInfinityOpen,
    language,
    openBrandVoicePopup,
  ]);

  return useMemo(
    () => ({
      projectType,
      availableBrandTermsCount,
      activeBrandVoices,
      detectedBrandTerms: notAppliedDetectedTerms,
      appliedDetectedTermIds,
      onApplyBrandTerm: onApplyDetectedBrandTerm,
      onBrandTermHover: onDetectedBrandTermHover,
      onBrandTermClick: onDetectedBrandTermClick,
      onSettingsClick: availableBrandTermsCount > 0 ? setupProjectVocabulary : null,
      onEmptyTermsClick: handleEmptyTermsClick,
      loading,
      disabled,
      readOnly,
      brandVoiceExistForLanguage,
      language,
    }),
    [
      projectType,
      availableBrandTermsCount,
      activeBrandVoices,
      notAppliedDetectedTerms,
      appliedDetectedTermIds,
      onApplyDetectedBrandTerm,
      onDetectedBrandTermHover,
      onDetectedBrandTermClick,
      setupProjectVocabulary,
      handleEmptyTermsClick,
      loading,
      disabled,
      readOnly,
      brandVoiceExistForLanguage,
      language,
    ]
  );
};
